<template>
    <div id='campaign-images-slide' class="swiper-container rounded-xl overflow-hidden bg-[#181C1F] campaign-slide">
    <!-- skeleton screen -->
    <div v-if="!state.images.length" class="aspect-video sm:aspect-[4/3] md:aspect-video bg-gray-50">
      <div class="w-full h-full flex items-center justify-center">
        <img src="../../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
      </div>
    </div>
    <div class="swiper-wrapper">
      <template v-for="image in state.images" :key="image.id">

        <!-- Image (attachment) -->
        <div v-if="image.type == 'attachment'" class="swiper-slide aspect-video block rounded-xl overflow-hidden bg-gray-50">
          <div class="swiper-lazy-preloader"></div>
          <img
            class='swiper-lazy w-full h-full object-cover'
            :alt="state.projectTitle + ' Campaign Project'"
            :src='image.webp_x3_url'
            :data-src='image.webp_x3_url'
          />
        </div>

        <!-- Video (非 attachment 就是影片) -->
        <div v-else class="swiper-slide aspect-video block rounded-xl overflow-hidden bg-[#181C1F]">
          <iframe
            width="100%"
            height="100%"
            :src="'https://www.youtube.com/embed/' + image.video"
            title="Campaign video player"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen>
          </iframe>
        </div>

      </template>
    </div>

    <div v-if='state.images.length > 1' class="swiper-button-prev">
      <div class='icon swiper-btn w-4 h-4 sm:w-6 sm:h-6 -rotate-90'></div>
    </div>

    <div v-if='state.images.length > 1' class="swiper-button-next">
      <div class='icon swiper-btn w-4 h-4 sm:w-6 sm:h-6 rotate-90'></div>
    </div>
    </div>
</template>

<script>
  import { defineComponent, ref, computed, onMounted, onUnmounted } from "vue";
  import Swiper, { Navigation, Pagination, Lazy } from 'swiper';
  import { useCampaignData } from '../../utils/useCampaignData';

  export default defineComponent({
    props: {
      campaignMode: {
        type: String,
        default: 'show',
        validator: (value) => ['show', 'preview', 'prelaunch'].includes(value)
      }
    },
    setup(props) {
      const state = ref({
        project_id: null,
        images: [],
        projectTitle: 'Campaign Project'
      })

      const swiperInstance = ref(null)

      const windowWidth = ref(window.innerWidth)

      const isMobile = computed(() => windowWidth.value < 560)
      const isTablet = computed(() => windowWidth.value >= 560 && windowWidth.value < 1024)

      const responsiveConfig = computed(() => ({
        spaceBetween: isMobile.value ? 8 : isTablet.value ? 12 : 16,
        touchRatio: isMobile.value ? 1.2 : 1,
        resistanceRatio: isMobile.value ? 0.85 : isTablet.value ? 0.7 : 0.5,
        threshold: isMobile.value ? 5 : undefined
      }))

      const updateScreenSize = () => {
        windowWidth.value = window.innerWidth
        // When the screen size changes, update the Swiper configuration
        if (swiperInstance.value) {
          updateSwiperConfig()
        }
      }

      const getCampaignImages = async () => {
        try {
          const { fetchCampaignData, campaignData } = useCampaignData(state.value.project_id, props.campaignMode)
          await fetchCampaignData()

          const data = campaignData.value || {}
          const title = data.title || 'Campaign Project'
          const slides = []

          if (data.embed_video_id) {
            slides.push({
              type: 'video',
              id: 'youtube',
              video: data.embed_video_id
            })
          }

          if (data.cover_image_src) {
            slides.push({
              type: 'attachment',
              id: 'cover',
              webp_x3_url: data.cover_image_src,
              alt: title
            })
          }

          if (Array.isArray(data.campaign_images)) {
            data.campaign_images.forEach((img) => {
              slides.push({
                type: 'attachment',
                id: img.id,
                webp_x3_url: img.webp_x3_url,
                alt: title
              })
            })
          }

          state.value.images = slides
          state.value.projectTitle = title
        } catch (error) {
          console.error('Failed to load campaign images:', error)
          state.value.images = []
        }
      }

      // Swiper configuration
      const getSwiperConfig = () => {
        const slideCount = state.value.images.length

        return {
          modules: [Navigation, Pagination, Lazy],
          lazy: {
            loadPrevNext: true,
            loadPrevNextAmount: 2,
            loadOnTransitionStart: true
          },
          loop: slideCount > 1,
          loopedSlides: slideCount,
          navigation: {
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
          },
          ...responsiveConfig.value,
          on: {
            init: function(sw) {
              updatePagination(sw, slideCount)
            },
            slideChange: function(sw) {
              updatePagination(sw, slideCount)
            },

          }
        }
      }

      // Update pagination display
      const updatePagination = (sw, slideCount) => {
        const pagy = document.querySelector('#campaign-images-slide .swiper-pagination span')
        if (pagy) {
          pagy.innerHTML = `${(sw.realIndex + 1).toString().padStart(2, '0')} / ${slideCount.toString().padStart(2, '0')}`
        }
      }

      // Update Swiper configuration when the screen size changes
      const updateSwiperConfig = () => {
        if (swiperInstance.value) {
          Object.assign(swiperInstance.value.params, responsiveConfig.value)
          swiperInstance.value.update()
        }
      }

      const campaignImageSlider = () => {
        const config = getSwiperConfig()

        swiperInstance.value = new Swiper('#campaign-images-slide', config)
      }

      onMounted(() => {
        const matches = window.location.href.match(/\/campaigns\/(\d+)/)
        state.value.project_id = matches ? matches[1] : 1

        getCampaignImages().then(() => {
          campaignImageSlider()
        })

        window.addEventListener('resize', updateScreenSize)
      })

      onUnmounted(() => {
        window.removeEventListener('resize', updateScreenSize)

        if (swiperInstance.value) {
          swiperInstance.value.destroy(true, true)
          swiperInstance.value = null
        }
      })

      return {
        state
      }
    }
  })
</script>

